import React, { useState, useRef, useEffect } from "react";
import { format } from "date-fns";

interface DateRangeFilterProps {
  startDate: string;
  endDate: string;
  onDateChange: (startDate: string, endDate: string) => void;
  onApply: () => void;
  onClear: () => void;
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  startDate,
  endDate,
  onDateChange,
  onApply,
  onClear,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Format date for display
  const formatDisplayDate = (dateString: string) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch {
      return "";
    }
  };

  // Get display text based on selected dates
  const getDisplayText = () => {
    if (startDate && endDate) {
      return `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`;
    }
    return "Filter by date";
  };

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Date picker button with calendar icon */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center rounded-lg border border-[var(--color-input-border)] bg-[var(--color-input-bg)] px-4 py-2.5 text-left text-sm font-medium text-theme-fg hover:bg-theme-secondary/10 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-0"
      >
        <svg
          className="mr-2 h-5 w-5 text-theme-fg-muted"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className="truncate">{getDisplayText()}</span>
        <svg
          className="ml-auto h-5 w-5 text-theme-fg-muted"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={isOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}
          />
        </svg>
      </button>

      {/* Date picker dropdown panel */}
      {isOpen && (
        <div className="absolute right-0 z-[100] mt-2 w-full origin-top-right rounded-lg border border-theme-border bg-theme-surface shadow-lg focus:outline-none md:w-96">
          <div className="p-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-sm font-medium text-theme-fg">
                Select Date Range
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-theme-fg-muted hover:text-theme-fg"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-theme-fg">
                    Start Date
                  </label>
                  <input
                    type="date"
                    className="w-full rounded-md border border-[var(--color-input-border)] bg-[var(--color-input-bg)] px-3 py-2 text-sm text-theme-fg shadow-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    value={startDate}
                    onChange={(e) => onDateChange(e.target.value, endDate)}
                    max={endDate || undefined}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-theme-fg">
                    End Date
                  </label>
                  <input
                    type="date"
                    className="w-full rounded-md border border-[var(--color-input-border)] bg-[var(--color-input-bg)] px-3 py-2 text-sm text-theme-fg shadow-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    value={endDate}
                    onChange={(e) => onDateChange(startDate, e.target.value)}
                    min={startDate || undefined}
                  />
                </div>
              </div>

              {/* Quick date ranges */}
              <div>
                <label className="mb-1 block text-sm font-medium text-theme-fg">
                  Quick Ranges
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const today = new Date();
                      const sevenDaysAgo = new Date(today);
                      sevenDaysAgo.setDate(today.getDate() - 7);

                      onDateChange(
                        sevenDaysAgo.toISOString().split("T")[0],
                        today.toISOString().split("T")[0]
                      );
                    }}
                    className="rounded-md bg-surface-ui px-2 py-1 text-xs font-medium text-theme-fg hover:bg-surface-ui-hover"
                  >
                    Last 7 days
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const today = new Date();
                      const thirtyDaysAgo = new Date(today);
                      thirtyDaysAgo.setDate(today.getDate() - 30);

                      onDateChange(
                        thirtyDaysAgo.toISOString().split("T")[0],
                        today.toISOString().split("T")[0]
                      );
                    }}
                    className="rounded-md bg-surface-ui px-2 py-1 text-xs font-medium text-theme-fg hover:bg-surface-ui-hover"
                  >
                    Last 30 days
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const today = new Date();
                      const firstDay = new Date(
                        today.getFullYear(),
                        today.getMonth(),
                        1
                      );

                      onDateChange(
                        firstDay.toISOString().split("T")[0],
                        today.toISOString().split("T")[0]
                      );
                    }}
                    className="rounded-md bg-surface-ui px-2 py-1 text-xs font-medium text-theme-fg hover:bg-surface-ui-hover"
                  >
                    This month
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-4 flex space-x-2">
              <button
                type="button"
                onClick={() => {
                  onClear();
                  setIsOpen(false);
                }}
                className="inline-flex w-full justify-center rounded-md border border-theme-border bg-theme-surface px-4 py-2 text-sm font-medium text-theme-fg shadow-sm hover:bg-theme-secondary/10 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => {
                  onApply();
                  setIsOpen(false);
                }}
                className="inline-flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-brand-primary border border-transparent rounded-md shadow-sm hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
                disabled={!startDate || !endDate}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
